import { DatePicker } from '@mui/lab';
import { Grid, useEventCallback } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import { DateTime } from 'luxon';
import AcquisitionSystemList from 'practice/components/shared/acquisition-system-list';
import { Outcome } from 'practice/constants/outcome';
import { TypeDomainPractice } from 'practice/constants/type-practice-domain';
import { memo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { regexOnlyNumber } from 'shell/constants/regex';
import { CirculationAreaClient } from 'shell/view-models/circulation-area-client';
import AddressAutoComplete from 'system/components/shared/search-address';
import StreetNumberList, { StreetNumberVM } from 'system/components/shared/street-number-list';
import { transformExponent } from 'system/components/shared/street-number-list/transform-exponent';
import * as yup from 'yup';

export const schema = yup.object().shape({
  date: yup.date().typeError('Formato non valido').nullable().required('Campo obbligatorio'),
  outcome: yup.string().nullable().required('Campo obbligatorio'),
  cap: yup
    .string()
    .nullable()
    .matches(regexOnlyNumber, 'Inserire numero codice avviamento postale'),
});

export interface IssuedAssessmentVM {
  date?: DateTime | null;
  outcome?: string;
  number?: number;
  exponent?: string;
  note?: string;
  noteMesso?: string;
  addressAuto?: CirculationAreaClient | null;
  cap?: string;
}

interface AccordionIssuedAssessmentFormProps {
  disabled?: boolean;
  values?: IssuedAssessmentVM;
  errors?: FormikErrors<IssuedAssessmentVM>;
  touched?: FormikTouched<IssuedAssessmentVM>;
  onChange: (
    key: keyof IssuedAssessmentVM,
    value: IssuedAssessmentVM[keyof IssuedAssessmentVM]
  ) => void;
  onChanges: (values: IssuedAssessmentVM) => void;
  isUnavailability?: boolean;
}

const AccordionIssuedAssessmentFormComponent = ({
  values,
  onChange,
  onChanges,
  touched,
  errors,
  isUnavailability,
  disabled,
}: AccordionIssuedAssessmentFormProps): React.ReactElement => {
  const onChangeAcquisitionSystem = useEventCallback((value: string | undefined) => {
    onChange('outcome', value);
  });

  const onChangeStreetNumberList = useEventCallback((item: StreetNumberVM | null) => {
    onChanges({
      number: item?.id ? Number.parseInt(item.id?.toString(), 10) : undefined,
      cap: item?.cap || '',
      exponent: item?.exponent,
    });
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <DatePicker
          label="Data accertamento"
          aria-label="Data accertamento"
          value={values?.date || null}
          onChange={(date) => {
            onChange('date', date);
          }}
          renderInput={(params) => (
            <PmiTextField
              id="effectiveDate"
              {...params}
              fullWidth
              error={touched?.date && Boolean(errors?.date)}
              helperText={errors?.date}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <AcquisitionSystemList
          label="Esito accertamento"
          onChange={onChangeAcquisitionSystem}
          value={values?.outcome || ''}
          typePractice={isUnavailability ? TypeDomainPractice.Tipo8 : TypeDomainPractice.Tipo7}
          error={touched?.outcome && Boolean(errors?.outcome)}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          id="noteDelMesso"
          label="Note del messo"
          aria-label="Note del messo"
          fullWidth
          multiline
          rows={5}
          onChange={(event) => {
            onChange('noteMesso', event.target.value);
          }}
          value={values?.noteMesso || ''}
          disabled
        />
      </Grid>
      {isUnavailability && values?.outcome === Outcome.CambioIndirizzo ? (
        <>
          <Grid item xs={12} md={6}>
            <AddressAutoComplete
              required
              selectedValue={values?.addressAuto || null}
              handleChange={(value) => {
                onChange('addressAuto', value);
              }}
              disabled={disabled}
              error={touched?.addressAuto && Boolean(errors?.addressAuto)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StreetNumberList
              label="Numero *"
              onChange={onChangeStreetNumberList}
              noEnded
              valueStreetNumber={
                values?.number
                  ? {
                      id: values?.number,
                      value: `${values?.number?.toString()}${transformExponent(
                        values?.exponent || ''
                      )}`,
                    }
                  : null
              }
              showExponent
              disabled={!values?.addressAuto?.code || disabled}
              circulationArea={values?.addressAuto?.code?.toString() || ''}
              error={touched?.number && Boolean(errors?.number)}
              helperText={errors?.number}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              label="C.A.P. *"
              aria-label="cap"
              id="cap"
              value={values?.cap || ''}
              onChange={(event) => {
                onChange('cap', event.target.value);
              }}
              fullWidth
              error={touched?.cap && Boolean(errors?.cap)}
              helperText={errors?.cap}
              disabled={disabled}
            />
          </Grid>
        </>
      ) : undefined}
      {isUnavailability && values?.outcome === Outcome.Altro ? (
        <>
          <Grid item xs={12}>
            <PmiTextField
              id="address"
              label="Altro *"
              aria-label="Altro"
              fullWidth
              inputProps={{
                maxLength: '250',
              }}
              onChange={(event) => {
                onChange('addressAuto', { description: event.target.value });
              }}
              value={values?.addressAuto?.description || ''}
              error={touched?.addressAuto && Boolean(errors?.addressAuto)}
              helperText={errors?.addressAuto}
            />
          </Grid>
        </>
      ) : undefined}
      <Grid item xs={12}>
        <PmiTextField
          id="note"
          label="Note"
          aria-label="Note"
          fullWidth
          multiline
          rows={7}
          inputProps={{
            maxLength: '240',
          }}
          onChange={(event) => {
            onChange('note', event.target.value);
          }}
          value={values?.note || ''}
          error={touched?.note && Boolean(errors?.note)}
          helperText={errors?.note}
        />
      </Grid>
    </Grid>
  );
};

AccordionIssuedAssessmentFormComponent.displayName = 'Accordion return issued form component';

const AccordionIssuedAssessmentForm = memo(AccordionIssuedAssessmentFormComponent);

export default AccordionIssuedAssessmentForm;
